import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { invariant } from '@e-socna/utils';
import { EMPTY_OBJECT } from '@e-socna/empty';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { LazyEditor } from '../../../../components/Editor/Lazy';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { MaterialForm_Material } from '../../fragments/material.fragment';

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
export const MaterialInfoFormStep = ({}: Readonly<Props>) => {
  const {
    errors,
    handleBlur,
    handleChange,
    initialValues,
    isSubmitting,
    touched,
    values,
  } = useFormikContext<MaterialForm_Material>();

  invariant('code' in values.codingConfig, 'Unexpected codingConfig type');

  const { fileDirectoryCategoriesCodeMap, materialFileCategoriesCodeMap } =
    useReferencesContext();

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          md: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'codingConfig.code') &&
              getIn(touched, 'codingConfig.code')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'codingConfig.code')}
          id="material-coding-config-code"
          label="Code"
          name="codingConfig.code"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.codingConfig.code}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 8,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={!!(errors.label && touched.label)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'label')}
          id="material-label"
          label="Libellé"
          name="label"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.label}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(errors.title && touched.title)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'title')}
          id="material-title"
          label="Titre"
          name="title"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.title}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={12}
        sx={{ display: 'flex', flexDirection: 'column', pt: 1, pb: 1 }}
      >
        <LazyEditor
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(errors.content
            ? {
                alert: {
                  severity: 'error',
                  content: errors.content,
                },
              }
            : EMPTY_OBJECT)}
          disabled={isSubmitting}
          error={!!(errors.content && touched.content)}
          id={values._id}
          initialValue={initialValues.content}
          label="Contenu"
          name="content"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          resolversSettings={{
            allowedReturnTypeIds: undefined,
            enableDirectGeneration: false,
            mustBeResolvableOnBack: false,
          }}
          storage={{
            directoryCategoryId: fileDirectoryCategoriesCodeMap.Material._id,
            entityId: values._id,
            imageCategory: materialFileCategoriesCodeMap.TextEditorImages,
          }}
          value={values.content}
        />
      </Grid>
    </Grid>
  );
};
